import { useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";

export const GREEN = "#167A5A";
export const BLUE = "#123B66";
export const ORANGE = "#F28C28";

export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("lp-visible")),
      { threshold: 0.12 });
    el.querySelectorAll(".lp-reveal").forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function Wordmark({ compact = false }) {
  return (
    <img
      src="/logo-esg-pmi.png"
      alt="ESG-PMI — Il pianeta sei tu · integrato con JOBTIMEHR"
      className={compact ? "h-9 w-auto" : "h-12 w-auto"}
      data-testid="wordmark"
    />
  );
}

export function SectionTitle({ kicker, title, sub, light = false }) {
  return (
    <div className="max-w-3xl">
      {kicker && (
        <p className="text-xs font-bold tracking-widest uppercase mb-3"
          style={{ color: light ? "#F5A94F" : ORANGE }}>
          {kicker}
        </p>
      )}
      <h2 className={`text-2xl md:text-4xl font-extrabold tracking-tight leading-tight ${light ? "text-white" : "text-slate-900"}`}
        style={{ fontFamily: "Manrope, sans-serif" }}>
        {title}
      </h2>
      {sub && (
        <p className={`mt-4 text-base md:text-lg leading-relaxed ${light ? "text-blue-100" : "text-slate-600"}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

export function FlowArrow() {
  return (
    <div className="flex justify-center py-1" aria-hidden="true">
      <svg width="20" height="28" viewBox="0 0 20 28" fill="none">
        <path d="M10 2v20m0 0l-6-6m6 6l6-6" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function ProgressBar({ pct, color = GREEN, label, testid }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="font-bold" style={{ color }} data-testid={testid}>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100">
        <div className="h-full rounded-full lp-progress" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export function HeroMockup() {
  return (
    <div className="lp-reveal rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 overflow-hidden"
      data-testid="hero-mockup" role="img"
      aria-label="Anteprima della dashboard ESG-PMI con completamento ESG 82%, VSME 87%, PMI-Banche 91%">
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-slate-100" style={{ background: BLUE }}>
        <span className="w-2.5 h-2.5 rounded-full bg-white/30" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/30" />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: ORANGE }} />
        <span className="ml-2 text-[10px] font-semibold text-white/80">ESG-PMI · Dashboard</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">ESG Completion</p>
            <p className="text-4xl font-extrabold" style={{ color: GREEN, fontFamily: "Manrope, sans-serif" }}>82%</p>
          </div>
          <div className="flex gap-1.5">
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ background: "#E7F4EE", color: GREEN }}>VSME 87%</span>
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ background: "#E9EFF6", color: BLUE }}>PMI-Banche 91%</span>
          </div>
        </div>
        <div className="space-y-2.5">
          <ProgressBar pct={87} label="VSME Basic" testid="mockup-vsme" />
          <ProgressBar pct={91} color={BLUE} label="Dossier PMI-Banche" testid="mockup-mef" />
          <ProgressBar pct={76} color="#1E9E76" label="Environmental" />
          <ProgressBar pct={94} color="#2F5D8A" label="Social" />
          <ProgressBar pct={68} color="#5B7CA3" label="Governance" />
        </div>
        <div className="grid grid-cols-3 gap-2 pt-1">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-center">
            <p className="text-[10px] text-amber-800 font-semibold">Dati mancanti</p>
            <p className="text-sm font-extrabold text-amber-900">6</p>
          </div>
          <div className="rounded-lg px-2 py-1.5 text-center" style={{ background: "#E7F4EE" }}>
            <p className="text-[10px] font-semibold" style={{ color: GREEN }}>JOBTIMEHR</p>
            <p className="text-[11px] font-bold" style={{ color: GREEN }}>Sincronizzato</p>
          </div>
          <div className="rounded-lg px-2 py-1.5 text-center" style={{ background: "#FBF0E1" }}>
            <p className="text-[10px] font-semibold" style={{ color: "#B85E0F" }}>Eva</p>
            <p className="text-[11px] font-bold" style={{ color: "#B85E0F" }}>Disponibile</p>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 italic text-center">Esempio di dashboard — dati dimostrativi.</p>
      </div>
    </div>
  );
}

export function EvaMark({ size = 44 }) {
  return (
    <span className="inline-flex items-center justify-center rounded-xl shrink-0"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${GREEN}, ${BLUE})` }}
      aria-hidden="true">
      <Sparkles className="text-white" style={{ width: size * 0.45, height: size * 0.45 }} />
    </span>
  );
}
